import { EmailIntegration, EmailReference, LexicalText } from "@/schema"
import { createEditor, type SerializedEditorState } from "lexical"

import { LEXICAL_NODES } from "@/components/lexical/config"
import { toHTML } from "@/components/lexical/serializer"

import { findIntegrationForEmail } from "./integration-email-utils"

export const getSignatureForEmail = (integration: EmailIntegration | null | undefined, email?: string | null) => {
  if (!integration) return undefined

  const normalizedEmail = email?.trim().toLowerCase()
  const aliasSignature = normalizedEmail
    ? integration.email_signatures?.find((signature) => signature.email_address?.toLowerCase() === normalizedEmail)
    : undefined

  return (
    aliasSignature ??
    integration.email_signature ??
    integration.email_signatures?.find((signature) => !signature.email_address)
  )
}

export const getIntegrationSignature = (emailIntegrations: EmailIntegration[] | undefined, from?: EmailReference) => {
  if (!emailIntegrations?.length) return undefined

  const fromEmail = from?.email || emailIntegrations[0]?.connected_email
  const integration = findIntegrationForEmail(emailIntegrations, fromEmail)
  return getSignatureForEmail(integration, fromEmail)?.content as LexicalText | undefined
}

export const ensureHtmlContent = (content?: LexicalText): LexicalText | undefined => {
  if (!content) return content
  if (content.html || !content.nodes) return content

  const editor = createEditor({ nodes: [...LEXICAL_NODES] })
  const editorState = editor.parseEditorState(JSON.stringify(content.nodes))
  editor.setEditorState(editorState)

  let html = ""
  editor.getEditorState().read(() => {
    html = toHTML(editor)
  })

  return { ...content, html, preferred_format: content.preferred_format ?? "html" }
}

export const signatureHtmlFromContent = (signature?: LexicalText) => {
  if (!signature) return ""
  const content = ensureHtmlContent(signature)
  return content?.html?.trim() || ""
}

export const removeTrailingSignature = (html: string, signatureHtml: string) => {
  if (!signatureHtml) return { bodyHtml: html, signatureHtml: "" }

  const normalizedBody = html.trimEnd()
  const normalizedSignature = signatureHtml.trimEnd()

  const index = normalizedBody.lastIndexOf(normalizedSignature)
  if (index === -1) {
    return { bodyHtml: html, signatureHtml: "" }
  }

  const bodyWithoutSignature = normalizedBody.slice(0, index).trimEnd()
  return { bodyHtml: bodyWithoutSignature, signatureHtml: normalizedSignature }
}

/**
 * Stringify an object with sorted keys for order-independent comparison.
 * This ensures {a:1, b:2} equals {b:2, a:1} when compared as strings.
 */
const stableStringify = (obj: unknown): string => {
  if (obj === null || typeof obj !== "object") {
    return JSON.stringify(obj)
  }
  if (Array.isArray(obj)) {
    return "[" + obj.map(stableStringify).join(",") + "]"
  }
  const sortedKeys = Object.keys(obj).sort()
  const parts = sortedKeys.map(
    (key) => JSON.stringify(key) + ":" + stableStringify((obj as Record<string, unknown>)[key])
  )
  return "{" + parts.join(",") + "}"
}

/**
 * Check if the signature nodes are at the TRAILING position of the content.
 * Uses stable stringification to handle different property ordering.
 */
export const containsSignatureNodes = (content?: SerializedEditorState, signature?: SerializedEditorState) => {
  if (!content || !signature) return false

  try {
    const contentChildren = content.root.children
    const sigChildren = signature.root.children

    if (sigChildren.length === 0 || sigChildren.length > contentChildren.length) return false

    const startIdx = contentChildren.length - sigChildren.length
    for (let i = 0; i < sigChildren.length; i++) {
      const contentNode = stableStringify(contentChildren[startIdx + i])
      const sigNode = stableStringify(sigChildren[i])
      if (contentNode !== sigNode) {
        return false
      }
    }
    return true
  } catch {
    return false
  }
}

export const removeTrailingSignatureNodes = (
  content?: SerializedEditorState,
  signature?: SerializedEditorState
): SerializedEditorState | undefined => {
  if (!containsSignatureNodes(content, signature)) return content

  return {
    ...content!,
    root: { ...content!.root, children: content!.root.children.slice(0, -signature!.root.children.length) },
  }
}
